'use client';

import { useState } from 'react';
import {
  FileArchive,
  FileSpreadsheet,
  ChevronUp,
  ChevronDown,
  Loader2,
  Mail,
  MailOpen,
} from 'lucide-react';
import { SelectionBar } from '@paeffgen/react/crm-documents/ui';

interface BelegeExportBarProps {
  count: number;
  onClear: () => void;
  onDownloadZip: () => void;
  onExportCsv: () => void;
  onExportLines: () => void;
  /** Auswahl als gelesen markieren. */
  onMarkRead?: () => void;
  /** Auswahl als ungelesen markieren. */
  onMarkUnread?: () => void;
  zipLoading?: boolean;
  csvLoading?: boolean;
  markLoading?: boolean;
  exportError?: string | null;
  /** Leiste auch ohne Auswahl zeigen (Auswahlmodus) — Aktionen dann deaktiviert. */
  open?: boolean;
}

export function BelegeExportBar({
  count,
  onClear,
  onDownloadZip,
  onExportCsv,
  onExportLines,
  onMarkRead,
  onMarkUnread,
  zipLoading,
  csvLoading,
  markLoading,
  exportError,
  open,
}: BelegeExportBarProps) {
  const [csvOpen, setCsvOpen] = useState(false);
  const noSelection = count === 0;

  return (
    <SelectionBar count={count} open={open} onClear={onClear}>
      {onMarkRead && (
        <button
          type="button"
          onClick={onMarkRead}
          disabled={noSelection || markLoading}
          aria-label="Als gelesen markieren"
          title="Als gelesen markieren"
          className="px-2.5 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-bold inline-flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white/10"
        >
          {markLoading ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <MailOpen size={14} />
          )}
          Gelesen
        </button>
      )}
      {onMarkUnread && (
        <button
          type="button"
          onClick={onMarkUnread}
          disabled={noSelection || markLoading}
          aria-label="Als ungelesen markieren"
          title="Als ungelesen markieren"
          className="px-2.5 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-bold inline-flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white/10"
        >
          <Mail size={14} />
          Ungelesen
        </button>
      )}
      <button
        type="button"
        onClick={onDownloadZip}
        disabled={noSelection || zipLoading}
        aria-label="Als ZIP herunterladen"
        className="px-2.5 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-bold inline-flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white/10"
      >
        {zipLoading ? (
          <Loader2 size={14} className="animate-spin" />
        ) : (
          <FileArchive size={14} />
        )}
        ZIP
      </button>
      <div className="relative">
        <button
          type="button"
          onClick={() => setCsvOpen((o) => !o)}
          disabled={noSelection || csvLoading}
          aria-label="CSV-Export"
          aria-expanded={csvOpen}
          className="px-2.5 py-2 rounded-lg bg-primary hover:bg-primary-hover text-sm font-bold inline-flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-primary"
        >
          {csvLoading ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <FileSpreadsheet size={14} />
          )}
          CSV
          {csvOpen ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
        </button>
        {csvOpen && (
          <div className="absolute right-0 bottom-full mb-2 w-56 bg-white rounded-lg shadow-xl border border-zinc-100 text-zinc-700 overflow-hidden">
            <button
              type="button"
              onClick={() => {
                onExportCsv();
                setCsvOpen(false);
              }}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-zinc-50 hover:text-primary"
            >
              <div className="font-medium">Übersichtsliste</div>
              <div className="text-xs text-zinc-500">Belegköpfe als CSV</div>
            </button>
            <button
              type="button"
              onClick={() => {
                onExportLines();
                setCsvOpen(false);
              }}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-zinc-50 hover:text-primary border-t border-zinc-50"
            >
              <div className="font-medium">Positionsdetails</div>
              <div className="text-xs text-zinc-500">Inkl. Belegpositionen</div>
            </button>
            {exportError && (
              <div className="px-4 py-2 text-xs text-red-600 border-t border-zinc-100">
                {exportError}
              </div>
            )}
          </div>
        )}
      </div>
    </SelectionBar>
  );
}
